import { I18n } from "@assets/strings";
import React, { JSX, useEffect, useState } from "react";
import styled from "styled-components";
import { useServices } from "@core/servicesContext";
import { Group, GroupMemberRole } from "@domain/group/group";
import { useGroupMembers } from "@domain/user/hooks";
import { useIsMobile } from "@utils/hooks";
import { PrimaryButton } from "@ui/components/buttons/primaryButton";
import { RoundedPlusButton } from "@ui/components/buttons/roundedPlusButton";
import { Primary } from "@ui/components/containers/primary";
import { RoundedBlock } from "@ui/components/containers/roundedBlock";
import { GroupMemberAccessModal } from "@ui/components/group/groupMemberAccessModal";
import { useMembersEditor } from "@ui/components/group/useMembersEditor";
import { ChevronDownIcon } from "@ui/components/icons";
import { row, stack } from "@ui/components/layout";
import { Menu } from "@ui/components/menus/menu";
import { DetailedMemberList } from "@ui/components/profile/community/detailedMemberList";
import { useModalTools } from "@ui/modals/modalContext";
import { useConfirmation } from "@ui/modals/useConfirmation";
import { mobile } from "@ui/styles/utils";
import { createToast } from "@ui/toasts/utils";
import { GroupAssociateOrganization } from "./groupAssociateOrganization";
import { GroupOwner } from "./groupOwner";

interface GroupCommunityProps extends React.HTMLAttributes<HTMLDivElement> {
	group: Group;
}
export const GroupCommunity: React.FC<GroupCommunityProps> = ({ group, ...htmlProps }) => {
	const [displayedRole, setDisplayedRole] = useState<GroupMemberRole>("MEMBER");
	const [showPending, setShowPending] = useState(false);
	const {
		result: members,
		totalElements,
		fetchMore,
		loading,
	} = useGroupMembers(group.id, showPending ? "pending" : displayedRole);

	const canSendShareroomMemberInvitation = group.license === "PREMIUM" && group.type === "SHAREROOM";
	const canEditMembers = canSendShareroomMemberInvitation
		? displayedRole !== "MEMBER"
		: group.userRole === "ADMINISTRATOR";
	const canSendInvitations = group?.type === "DISCUSSION_GROUP" && group?.accessMode === "OPEN" && !canEditMembers;

	const { userService } = useServices();
	const { createModal } = useModalTools();
	const askDeleteConfirmation = useConfirmation(
		I18n.t(`members.${group.type}.${displayedRole}.delete.title`),
		I18n.t("delete")
	);
	const openMembersModal = useMembersEditor(group, displayedRole);
	const availableTabs = ["MEMBER", "ADMINISTRATOR"] as const;
	const isMobile = useIsMobile();

	useEffect(() => {
		if (showPending) {
			setDisplayedRole("MEMBER");
		}
	}, [showPending]);

	const renderTabs = (): JSX.Element => (
		<Title>
			{availableTabs.map((role) => (
				<MemberTab
					selected={!showPending && role === displayedRole}
					key={role}
					onClick={() => {
						setDisplayedRole(role);
						setShowPending(false);
					}}
				>
					{I18n.t(`members.${group.type}.${role}.list.title`, {
						count: (role === "MEMBER" ? group.memberCounts.MEMBER : group.memberCounts.ADMINISTRATOR) ?? 0,
					})}
				</MemberTab>
			))}
			{canEditMembers && (
				<MemberTab selected={showPending} onClick={() => setShowPending(true)}>
					{I18n.t("members.pending.list.title", {
						count:
							(group.statusCounts.PENDING_GUEST_ACCEPTANCE ?? 0) + (group.statusCounts.PENDING_HOST_ACCEPTANCE ?? 0),
					})}
				</MemberTab>
			)}
		</Title>
	);

	return (
		<Container>
			<GroupMembers {...htmlProps}>
				{canEditMembers && !showPending && (
					<StyledEditButton
						label={I18n.t(`members.${group.type}.${displayedRole}.add.button`)}
						onClick={() => openMembersModal(I18n.t(`members.${group.type}.${displayedRole}.add.title`))}
					/>
				)}
				{!isMobile ? (
					renderTabs()
				) : (
					<Menu bottomSheetMode closeOnSelect align="left" renderMenuContent={renderTabs}>
						<SelectorContainer hasOffset={canEditMembers && !showPending}>
							<div>
								{!showPending
									? I18n.t(`members.${group.type}.${displayedRole}.list.title`, {
											count:
												(displayedRole === "MEMBER" ? group.memberCounts.MEMBER : group.memberCounts.ADMINISTRATOR) ??
												0,
										})
									: I18n.t("members.pending.list.title", {
											count:
												(group.statusCounts.PENDING_GUEST_ACCEPTANCE ?? 0) +
												(group.statusCounts.PENDING_HOST_ACCEPTANCE ?? 0),
										})}
							</div>
							<ChevronDownIcon />
						</SelectorContainer>
					</Menu>
				)}
				{!showPending && totalElements === 0 && canEditMembers && (
					<PrimaryButton onClick={() => openMembersModal(I18n.t(`members.${group.type}.${displayedRole}.add.title`))}>
						{I18n.t(`members.${group.type}.${displayedRole}.add.button`)}
					</PrimaryButton>
				)}
				<DetailedMemberList
					loading={loading}
					onEndReached={fetchMore}
					members={members}
					protectedMember={group.owner.id}
					acceptMember={
						canEditMembers
							? (member) =>
									createModal((close) => (
										<GroupMemberAccessModal
											close={close}
											groupType={group.type}
											groupId={group.id}
											groupName={group.name}
											member={member}
										/>
									))
							: undefined
					}
					deleteMember={
						canEditMembers
							? async (id, role) => {
									askDeleteConfirmation(async () => {
										try {
											await userService.deleteMemberFromGroup(group.id, id, role ?? displayedRole, showPending);
											createToast(
												I18n.t("success"),
												I18n.t(`members.${group.type}.${displayedRole}.delete.success`),
												"success"
											);
										} catch {
											createToast(I18n.t("error"), I18n.t(`members.delete.error`), "error");
										}
									});
								}
							: undefined
					}
				/>
			</GroupMembers>
			<RightCell>
				{canSendInvitations && (
					<InvitationButton onClick={() => openMembersModal(I18n.t("group.DISCUSSION_GROUP.public.invitation_button"))}>
						{I18n.t("group.DISCUSSION_GROUP.public.invitation_button")}
					</InvitationButton>
				)}
				<GroupOwner group={group} />
			</RightCell>

			<GroupAssociateOrganizationCell group={group} />
		</Container>
	);
};

const Container = styled.div`
	display: grid;
	column-gap: 45px;
	row-gap: 15px;
	grid-template-columns: minmax(0, 1fr) 280px;
	grid-template-rows: auto;
	grid-template-areas:
		"members owner"
		"members organization"
		"members .";

	@media ${mobile} {
		grid-template-columns: 100%;
		grid-template-areas:
			"owner"
			"organization"
			"members";
	}
`;

const SelectorContainer = styled(Primary)<{ hasOffset?: boolean }>`
	${row(10, "space-between", "center")};
	padding: 15px 16px;
	border-radius: 7px;
	margin: 20px;
	margin-top: ${({ hasOffset }) => (hasOffset ? 50 : 0)}px;
	font-weight: bold;
	border: solid 1px ${({ theme }) => theme.colors.lightergray};

	> svg {
		width: 14px;
		path {
			fill: ${({ theme }) => theme.colors.textSecondary};
		}
		display: block;
	}
`;

const GroupMembers = styled(RoundedBlock)`
	grid-area: members;
	position: relative;
	padding: 20px 0;
	> button {
		margin: 60px auto;
	}
`;

const StyledEditButton = styled(RoundedPlusButton)`
	position: absolute;
	right: 20px;
	top: 20px;
`;

const RightCell = styled.div`
	grid-area: owner;
`;

const GroupAssociateOrganizationCell = styled(GroupAssociateOrganization)`
	grid-area: organization;
`;

const Title = styled.div`
	padding: 40px;
	${row(60)};
	@media ${mobile} {
		padding: 20px;
		${stack(20)};
	}
`;

const MemberTab = styled.div.withConfig({
	shouldForwardProp: (prop) => prop !== "selected",
})<{ selected: boolean }>`
	cursor: pointer;
	${({ theme }) => theme.textStyles.desktopSubtitle}
	color: ${({ theme, selected }) => (selected ? theme.colors.primary : theme.colors.textPrimary)};
	font-weight: 600;
`;

const InvitationButton = styled(PrimaryButton)`
	width: 100%;
	margin-bottom: 30px;
`;
